import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../shared/api.service';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

interface CreateDataPayload {
    name: string;
    code: string;
    status: 'active' | 'inactive';
}

interface PaymentGateway {
    id: string;
    name: string;
    code: string;
    status: 'active' | 'inactive';
}

interface Pagination {
    current_page: number;
    per_page: number;
    total: number;
    total_page: number;
}

@Component({
  selector: 'app-payment-gateway',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './payment-gateway.component.html',
  styleUrl: './payment-gateway.component.scss'
})
export class PaymentGatewayComponent {
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;
    selectedPaymentGatewayId: string = '';

    paymentGateway: PaymentGateway[] = [];
    pagination: Pagination = {
        current_page: 1,
        per_page: 100,
        total: 0,
        total_page: 1
    };

    createDataForm: CreateDataPayload = {
        name: '',
        code: '',
        status: 'inactive'
    }

    constructor(private apiService: ApiService, private router: Router) { }

    ngOnInit(): void {
        this.getPaymentGateways();
    }

    async getPaymentGateways(): Promise<void> {
        try {
            const response = await this.apiService.get('/api/v1/tenant/manage/payment-gateway/list?page=1&per_page=100');
            // console.log('Payment Gateways:', response);
            this.paymentGateway = response.data.list;
            this.pagination = response.data.pagination;
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat mengambil data payment gateway.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async getPaymentGatewayById(paymentGatewayId: string): Promise<void> {
        try {
            const response = await this.apiService.get(`/api/v1/tenant/manage/payment-gateway/${paymentGatewayId}`);
            // console.log('Payment Gateway Details:', response);

            this.createDataForm = {
                name: response.data.name,
                code: response.data.code,
                status: response.data.status
            }

            this.selectedPaymentGatewayId = paymentGatewayId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat mengambil data payment gateway.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async createData(): Promise<void> {
        if (!this.validateDataForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/tenant/manage/payment-gateway', this.createDataForm);
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Payment gateway berhasil ditambahkan.',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false
            });
            this.closeModal();
            this.getPaymentGateways();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat menambahkan payment gateway.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    async updateData(): Promise<void> {
        if (!this.validateDataForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(`/api/v1/tenant/manage/payment-gateway/${this.selectedPaymentGatewayId}`, this.createDataForm);
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Payment gateway berhasil diperbarui.',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false
            });
            this.closeModal();
            this.getPaymentGateways();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memperbarui payment gateway.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        } finally {
            this.isSubmitting = false;
        }
    }

    async deleteData(paymentGatewayId: string, paymentGatewayName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus payment gateway ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });
        if (result.isConfirmed) {
            try {
                await this.apiService.delete(`/api/v1/tenant/manage/payment-gateway`, paymentGatewayId);
                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Payment gateway berhasil dihapus.',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false
                });
                this.getPaymentGateways();
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error?.error?.message || 'Terjadi kesalahan saat menghapus payment gateway.',
                    confirmButtonColor: '#dc3545',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        }
    }

    openaddModal(): void {
        this.isModalOpen = true;
        this.resetForm();
    }

    openeditModal(paymentGatewayId: string): void {
        this.getPaymentGatewayById(paymentGatewayId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.createDataForm = {
            name: '',
            code: '',
            status: 'inactive'
        };
    }

    validateDataForm(): boolean {
        if (!this.createDataForm.name || !this.createDataForm.code || !this.createDataForm.status) {
            Swal.fire({
                icon: 'warning',
                title: 'Peringatan',
                text: 'Semua field harus diisi.',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }
        return true;
    }

    async goToPage(page: number): Promise<void> {
        if (page < 1 || page > this.pagination.total_page) {
            return;
        }
        this.pagination.current_page = page;
        await this.getPaymentGateways();
    }

    async nextPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page + 1);
    }

    async previousPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page - 1);
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateData();
        } else {
            await this.createData();
        }
    }
}
